import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from '../utils/i18n-utils';
import { useTheme } from '@mui/material';
import {
  Box,
  Button,
  Paper,
  Typography,
  TextField,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  FormControlLabel,
  Checkbox,
  Tab,
  Tabs,
  Snackbar,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Print as PrintIcon,
  Search as SearchIcon,
  Person as PersonIcon,
  Receipt as ReceiptIcon,
  PictureAsPdf as PdfIcon,
  Close as CloseIcon,
  PersonAdd as PersonAddIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { getAllCarMakes } from '../api/carMake';
import { getAllCarModels } from '../api/carModel';
import { getAllCustomers, searchCustomers, createCustomer, deleteCustomer } from '../api/customer';
import { getAllProducts } from '../api/product';
import { jsPDF } from "jspdf";
import 'jspdf-autotable';
import { format } from 'date-fns';
import { autoTable } from 'jspdf-autotable';
import { createSale, getAllSales, deleteSale, updateSale } from '../api/sale';
import { useAuth } from '../context/AuthContext';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`sales-tabpanel-${index}`}
      aria-labelledby={`sales-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

interface Product {
  id: number;
  carMake?: string;
  carMakeId?: number;
  carMake_id?: number;
  carMakeObj?: { id: number; name: string };
  model?: string;
  carModelId?: number;
  carModel_id?: number;
  carModelObj?: { id: number; name: string; carMakeId: number };
  year?: string;
  glassModel?: string;
  glassType?: string;
  coveredTo?: string;
  size?: string;
  sz?: string;
  acc?: string;
  remark?: string;
  itemPrice: number;
  fittingPrice: number;
  quantity: number;
  minQuantity?: number;
  supplier?: string;
  supplierPrice?: number;
  // Display properties
  displayMake?: string;
  displayModel?: string;
}

interface Customer {
  id?: number;
  name: string;
  firstName?: string;
  lastName?: string;
  phone: string;
  carMakeId?: number;
  carModelId?: number;
  carMake?: string;
  carModel?: string;
}

interface SaleItem {
  id?: number;
  product: Product;
  quantity: number;
  price: number;
  includeFitting: boolean;
  total: number;
}

interface Invoice {
  id: number;
  customer: Customer;
  items: SaleItem[];
  date: Date;
  totalAmount: number;
  paymentMethod: string;
  notes: string;
  status: 'pending' | 'completed' | 'cancelled';
  paymentStatus: 'unpaid' | 'paid';
  invoiceNumber?: string;
}

interface CarMake {
  id: number;
  name: string;
}

interface CarModel {
  id: number;
  name: string;
  carMakeId: number;
}

const Sales: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isRTL = theme.direction === 'rtl';
  const { user } = useAuth();
  const isManager = user?.role === 'manager' || user?.role === 'admin';

  // Tab state
  const [tabValue, setTabValue] = useState(0);

  // Customer state
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [newCustomer, setNewCustomer] = useState<Customer>({ 
    name: '', 
    firstName: '', 
    lastName: '', 
    phone: '' 
  });
  const [showNewCustomerForm, setShowNewCustomerForm] = useState(false);
  
  // Car details state
  const [carMakes, setCarMakes] = useState<CarMake[]>([]);
  const [carModels, setCarModels] = useState<CarModel[]>([]);
  const [selectedCarMake, setSelectedCarMake] = useState<CarMake | null>(null);
  const [selectedCarModel, setSelectedCarModel] = useState<CarModel | null>(null);
  const [filteredCarModels, setFilteredCarModels] = useState<CarModel[]>([]);
  
  // Product state
  const [products, setProducts] = useState<Product[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [quantity, setQuantity] = useState<number>(1);
  const [includeFitting, setIncludeFitting] = useState<boolean>(false);
  const [selectedGlassType, setSelectedGlassType] = useState<string>('');
  const [customerYear, setCustomerYear] = useState<string>('');
  
  // Sale state
  const [saleItems, setSaleItems] = useState<SaleItem[]>([]);
  const [date, setDate] = useState<Date | null>(new Date());
  const [paymentMethod, setPaymentMethod] = useState<string>('cash');
  const [notes, setNotes] = useState<string>('');
  
  // Invoice state
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  
  // UI state
  const [loading, setLoading] = useState<boolean>(false);
  const [snackbar, setSnackbar] = useState({ 
    open: false, 
    message: '', 
    severity: 'success' as 'success' | 'error' | 'info' | 'warning'
  });
  const [openProductDialog, setOpenProductDialog] = useState(false);
  const [openInvoiceDialog, setOpenInvoiceDialog] = useState(false);

  // New state for invoice preview
  const [showInvoicePreview, setShowInvoicePreview] = useState(false);

  // Add state for invoice filters
  const [invoiceFilters, setInvoiceFilters] = useState({
    startDate: null as Date | null,
    endDate: null as Date | null,
    status: 'all' as 'all' | 'pending' | 'completed' | 'cancelled'
  });

  // New state for new customer dialog
  const [openNewCustomerDialog, setOpenNewCustomerDialog] = useState(false);
  
  // Delete sale confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [saleToDelete, setSaleToDelete] = useState<Invoice | null>(null);
  
  // Delete customer confirmation dialog state
  const [deleteCustomerDialogOpen, setDeleteCustomerDialogOpen] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState<Customer | null>(null);

  // Load the last active tab from localStorage
  useEffect(() => {
    try {
      // Clean up any existing invoices in localStorage 
      if (localStorage.getItem('moftahInvoices')) {
        console.log('Removing obsolete invoices from localStorage');
        localStorage.removeItem('moftahInvoices');
      }
      
      const savedTab = localStorage.getItem('lastActiveTab');
      if (savedTab !== null) {
        setTabValue(parseInt(savedTab, 10));
        console.log(`Loaded last active tab: ${savedTab}`);
      }
    } catch (error) {
      console.error('Error loading last active tab from localStorage:', error);
    }
  }, []);

  // Save the active tab whenever it changes
  useEffect(() => {
    localStorage.setItem('lastActiveTab', tabValue.toString());
  }, [tabValue]);

  // Load sales from API on component mount
  useEffect(() => {
    const loadSalesFromAPI = async () => {
      try {
        setLoading(true);
        console.log("Fetching sales from API...");
        const data = await getAllSales();
        
        if (Array.isArray(data) && data.length > 0) {
          // Process sales from API into the Invoice format
          const processedSales = data.map(sale => {
            // Ensure Customer object is not null
            const customerData = sale.Customer || { name: 'Unknown Customer', phone: 'N/A' };
            
            // Process sale items with null checks
            const processedItems = sale.SaleItems && Array.isArray(sale.SaleItems) 
              ? sale.SaleItems.map((item: any) => {
                  // Make sure the Product exists
                  const productData = item.Product || {
                    id: 0,
                    carMake: 'Unknown',
                    model: 'Unknown',
                    itemPrice: 0,
                    fittingPrice: 0,
                    quantity: 0
                  };
                  
                  // Create a safe product with all possible null values handled
                  const safeProduct = {
                    ...productData,
                    displayMake: productData.carMakeObj?.name || productData.carMake || 'Unknown',
                    displayModel: productData.carModelObj?.name || productData.model || 'Unknown'
                  };
                  
                  return {
                    id: item.id || 0,
                    product: safeProduct,
                    quantity: item.quantity || 1,
                    price: item.unitPrice || 0,
                    includeFitting: item.includeFitting || false,
                    total: item.totalPrice || 0
                  };
                }) 
              : [];
            
            return {
              id: sale.id || 0,
              invoiceNumber: sale.invoiceNumber || `INV-${Date.now()}`,
              customer: customerData,
              items: processedItems,
              date: new Date(sale.date || new Date()),
              totalAmount: sale.totalAmount || 0,
              paymentMethod: sale.paymentMethod || 'cash',
              notes: sale.notes || '',
              status: sale.status || 'completed',
              paymentStatus: sale.paymentStatus || 'unpaid'
            };
          });
          
          setInvoices(processedSales);
          console.log(`Loaded ${processedSales.length} sales from API`);
        } else {
          // If no sales in API, display empty state
          console.log('No sales found in database');
          setInvoices([]);
        }
      } catch (error) {
        console.error('Error fetching sales from API:', error);
        setSnackbar({ 
          open: true, 
          message: 'Failed to fetch sales from database.', 
          severity: 'error' 
        });
        
        // Empty the invoices array
        setInvoices([]);
      } finally {
        setLoading(false);
      }
    };

    // Call functions to load data
    fetchCarMakes();
    fetchCarModels();
    fetchCustomers();
    fetchProducts();
    loadSalesFromAPI();
    loadDraftSaleData(); // Only for in-progress sale drafts, not completed invoices
    loadFormDefaults();
  }, []);

  // Load saved filter preferences from localStorage
  useEffect(() => {
    try {
      const savedFilters = localStorage.getItem('invoiceFilters');
      if (savedFilters) {
        const parsedFilters = JSON.parse(savedFilters);
        
        // Convert string dates back to Date objects if they exist
        const processedFilters = {
          ...parsedFilters,
          startDate: parsedFilters.startDate ? new Date(parsedFilters.startDate) : null,
          endDate: parsedFilters.endDate ? new Date(parsedFilters.endDate) : null
        };
        
        setInvoiceFilters(processedFilters);
        console.log('Loaded invoice filter preferences from localStorage');
      }
    } catch (error) {
      console.error('Error loading invoice filter preferences from localStorage:', error);
    }
  }, []);

  // Save filter preferences whenever they change
  useEffect(() => {
    try {
      localStorage.setItem('invoiceFilters', JSON.stringify(invoiceFilters));
    } catch (error) {
      console.error('Error saving invoice filter preferences to localStorage:', error);
    }
  }, [invoiceFilters]);

  // Fetch customers from API with validation
  const fetchCustomers = async () => {
    try {
      setLoading(true);
      console.log("Fetching all customers...");
      const data = await getAllCustomers();
      console.log(`Customers fetched: ${data.length} customers found`);
      
      if (!Array.isArray(data)) {
        console.error("Invalid customer data returned from API:", data);
        setSnackbar({ open: true, message: 'Invalid customer data format received', severity: 'error' });
        return;
      }
      
      setCustomers(data);
    } catch (error: any) {
      console.error("Error fetching customers:", error);
      setSnackbar({ open: true, message: 'Failed to fetch customers: ' + (error.message || 'Unknown error'), severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Search customers by name or phone with more robust error handling
  const handleCustomerSearch = async (searchTerm: string) => {
    if (!searchTerm || searchTerm.length < 2) {
      // If search term is cleared or too short, reload all customers
      if (searchTerm === '') {
        fetchCustomers();
      }
      return;
    }
    
    try {
      setLoading(true);
      console.log(`Searching customers with term: "${searchTerm}"`);
      const data = await searchCustomers(searchTerm);
      
      if (!Array.isArray(data)) {
        console.error("Invalid search results returned from API:", data);
        setSnackbar({ open: true, message: 'Invalid search results format', severity: 'error' });
        return;
      }
      
      console.log(`Customer search results: ${data.length} customers found for "${searchTerm}"`);
      setCustomers(data);
    } catch (error: any) {
      console.error("Error searching customers:", error);
      setSnackbar({ open: true, message: 'Search failed: ' + (error.message || 'Unknown error'), severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Fetch car makes from API
  const fetchCarMakes = async () => {
    try {
      setLoading(true);
      const data = await getAllCarMakes();
      console.log("Car makes fetched:", data);
      setCarMakes(data);
    } catch (error) {
      console.error("Error fetching car makes:", error);
      setSnackbar({ open: true, message: 'Failed to fetch car makes', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Fetch car models from API
  const fetchCarModels = async () => {
    try {
      setLoading(true);
      const data = await getAllCarModels();
      console.log("Car models fetched:", data);
      setCarModels(data);
    } catch (error) {
      console.error("Error fetching car models:", error);
      setSnackbar({ open: true, message: 'Failed to fetch car models', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Filter car models when car make changes
  useEffect(() => {
    if (selectedCarMake) {
      const filtered = carModels.filter(model => model.carMakeId === selectedCarMake.id);
      setFilteredCarModels(filtered);
    } else {
      setFilteredCarModels([]);
    }
  }, [selectedCarMake, carModels]);

  // Handle tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    // The active tab is saved by the useEffect above
  };

  // Handle adding a new customer with better feedback
  const handleAddCustomer = async () => {
    if (!newCustomer.firstName || !newCustomer.phone) {
      setSnackbar({ open: true, message: 'First name and phone are required', severity: 'error' });
      return;
    }

    try {
      setLoading(true);
      console.log("Creating new customer:", newCustomer);
      
      // Make sure name field is set correctly
      const fullName = `${newCustomer.firstName} ${newCustomer.lastName || ''}`.trim();
      
      const customerData = {
        name: fullName,
        firstName: newCustomer.firstName,
        lastName: newCustomer.lastName,
        phone: newCustomer.phone,
        carMake: selectedCarMake?.name || '',
        carModel: selectedCarModel?.name || ''
      };
      
      // Save vehicle details to localStorage for future use
      if (selectedCarMake || selectedCarModel) {
        const vehicleDetails = {
          carMakeId: selectedCarMake?.id || null,
          carModelId: selectedCarModel?.id || null
        };
        localStorage.setItem('lastVehicleDetails', JSON.stringify(vehicleDetails));
      }
      
      const response = await createCustomer(customerData);
      console.log("Customer created successfully:", response);
      
      if (!response || !response.customer) {
        throw new Error('Invalid response from server when creating customer');
      }
      
      // Add the new customer to the list and select it
      const createdCustomer = response.customer;
      setCustomers(prevCustomers => [...prevCustomers, createdCustomer]);
      setSelectedCustomer(createdCustomer);
      
      // Reset form
      setShowNewCustomerForm(false);
      setNewCustomer({ name: '', firstName: '', lastName: '', phone: '' });
      
      setSnackbar({ open: true, message: 'Customer added successfully', severity: 'success' });
      
      // Refresh customer list to ensure we have the latest data
      fetchCustomers();
    } catch (error: any) {
      console.error("Error creating customer:", error);
      setSnackbar({ open: true, message: 'Failed to create customer: ' + (error.message || 'Unknown error'), severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Load last vehicle details when opening the new customer form
  useEffect(() => {
    if (showNewCustomerForm) {
      try {
        const savedVehicleDetails = localStorage.getItem('lastVehicleDetails');
        if (savedVehicleDetails) {
          const details = JSON.parse(savedVehicleDetails);
          
          // Set car make if available
          if (details.carMakeId) {
            const make = carMakes.find(m => m.id === details.carMakeId);
            if (make) {
              setSelectedCarMake(make);
            }
          }
          
          console.log('Loaded last vehicle details from localStorage');
        }
      } catch (error) {
        console.error('Error loading vehicle details from localStorage:', error);
      }
    }
  }, [showNewCustomerForm, carMakes]);
  
  // Set car model once carMakeId is set and models are filtered
  useEffect(() => {
    if (showNewCustomerForm && selectedCarMake) {
      try {
        const savedVehicleDetails = localStorage.getItem('lastVehicleDetails');
        if (savedVehicleDetails) {
          const details = JSON.parse(savedVehicleDetails);
          
          // Set car model if its make matches the selected make
          if (details.carModelId && filteredCarModels.length > 0) {
            const model = filteredCarModels.find(m => m.id === details.carModelId);
            if (model) {
              setSelectedCarModel(model);
            }
          }
        }
      } catch (error) {
        console.error('Error setting car model from localStorage:', error);
      }
    }
  }, [filteredCarModels, showNewCustomerForm, selectedCarMake]);

  // Fetch products from API
  const fetchProducts = async () => {
    try {
      setLoading(true);
      console.log("Fetching all products...");
      const data = await getAllProducts();
      console.log(`Products fetched: ${data.length} products found`);
      
      if (!Array.isArray(data)) {
        console.error("Invalid product data returned from API:", data);
        setSnackbar({ open: true, message: 'Invalid product data format received', severity: 'error' });
        return;
      }
      
      // Process products to make them compatible with the UI
      const processedProducts = data.map((product: any) => {
        // Safely check if objects exist before accessing their properties
        const carMakeName = product.carMakeObj && product.carMakeObj.name ? product.carMakeObj.name : product.carMake || '';
        const carModelName = product.carModelObj && product.carModelObj.name ? product.carModelObj.name : product.model || '';
        
        return {
          ...product,
          // Use the safely extracted display names
          displayMake: carMakeName,
          displayModel: carModelName,
          // Map price fields for the sales UI
          price: product.itemPrice || 0,
        };
      });
      
      setProducts(processedProducts);
    } catch (error: any) {
      console.error("Error fetching products:", error);
      setSnackbar({ open: true, message: 'Failed to fetch products: ' + (error.message || 'Unknown error'), severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Track product selection history
  const trackProductSelection = (product: Product) => {
    try {
      const productHistory = JSON.parse(localStorage.getItem('productSelectionHistory') || '{}');
      
      // Increment the selection count for this product
      const productId = product.id.toString();
      productHistory[productId] = (productHistory[productId] || 0) + 1;
      
      // Store timestamp of last selection
      productHistory[`${productId}_lastSelected`] = new Date().getTime();
      
      localStorage.setItem('productSelectionHistory', JSON.stringify(productHistory));
      console.log(`Updated product selection history for product ID ${productId}`);
    } catch (error) {
      console.error('Error tracking product selection:', error);
    }
  };
  
  // Sort products based on selection history
  const getSortedProducts = (): Product[] => {
    try {
      // If products is empty or undefined, return empty array
      if (!products || products.length === 0) {
        return [];
      }
      
      const productHistory = JSON.parse(localStorage.getItem('productSelectionHistory') || '{}');
      
      // Create a copy of products array to sort
      return [...products].sort((a, b) => {
        // Make sure a and b are valid products with IDs
        if (!a || !a.id) return 1;  // Move invalid items to end
        if (!b || !b.id) return -1; // Move invalid items to end
        
        const aId = a.id.toString();
        const bId = b.id.toString();
        
        const aCount = productHistory[aId] || 0;
        const bCount = productHistory[bId] || 0;
        
        // If counts are equal, sort by last selected time
        if (aCount === bCount) {
          const aLastSelected = productHistory[`${aId}_lastSelected`] || 0;
          const bLastSelected = productHistory[`${bId}_lastSelected`] || 0;
          return bLastSelected - aLastSelected; // More recently selected first
        }
        
        return bCount - aCount; // Higher count first
      });
    } catch (error) {
      console.error('Error sorting products by history:', error);
      return products || [];
    }
  };

  // Get filtered products based on user selections
  const getFilteredProducts = (): Product[] => {
    if (filteredProducts.length > 0) {
      return filteredProducts;
    }
    return getSortedProducts();
  };

  // Filter products based on car make, model, glass type, and year compatibility
  const filterProductsByCompatibility = () => {
    let filtered = [...products];
    
    // Filter by car make
    if (selectedCarMake) {
      filtered = filtered.filter(p => 
        p.carMakeId === selectedCarMake.id || 
        p.carMake_id === selectedCarMake.id
      );
    }
    
    // Filter by car model
    if (selectedCarModel) {
      filtered = filtered.filter(p => 
        p.carModelId === selectedCarModel.id || 
        p.carModel_id === selectedCarModel.id
      );
    }
    
    // Filter by glass type
    if (selectedGlassType) {
      filtered = filtered.filter(p => p.glassType === selectedGlassType);
    }
    
    // Filter by year compatibility if year is provided
    if (customerYear) {
      const customerYearNum = parseInt(customerYear, 10);
      if (!isNaN(customerYearNum)) {
        filtered = filtered.filter(p => {
          const productYearStart = p.year ? parseInt(p.year, 10) : null;
          const productYearEnd = p.coveredTo ? parseInt(p.coveredTo, 10) : null;
          
          // If product has no year data, include it in results
          if (productYearStart === null && productYearEnd === null) {
            return true;
          }
          
          // If product has only start year
          if (productYearStart !== null && productYearEnd === null) {
            return customerYearNum >= productYearStart;
          }
          
          // If product has only end year
          if (productYearStart === null && productYearEnd !== null) {
            return customerYearNum <= productYearEnd;
          }
          
          // If product has both start and end years
          return customerYearNum >= productYearStart! && customerYearNum <= productYearEnd!;
        });
      }
    }
    
    setFilteredProducts(filtered);
  };

  // Handle adding a new sale item
  const handleAddItem = () => {
    if (!selectedProduct) {
      setOpenProductDialog(true);
      return;
    }

    // Calculate the total price, including fitting if selected
    const productPrice = selectedProduct.itemPrice || 0;
    const fittingPrice = includeFitting ? (selectedProduct.fittingPrice || 0) : 0;
    const totalPrice = productPrice + fittingPrice;

    const newItem: SaleItem = {
      product: selectedProduct,
      quantity,
      price: productPrice,
      includeFitting,
      total: totalPrice * quantity
    };

    // Track this product selection in history
    trackProductSelection(selectedProduct);

    setSaleItems([...saleItems, newItem]);
    
    // Reset form values
    setSelectedProduct(null);
    setQuantity(1);
    setIncludeFitting(false);
    setFilteredProducts([]);
    setSelectedCarMake(null);
    setSelectedCarModel(null);
    setSelectedGlassType('');
    setCustomerYear('');
    
    setOpenProductDialog(false);
  };

  // Handle removing a sale item
  const handleRemoveItem = (index: number) => {
    setSaleItems((prev) => prev.filter((_, i) => i !== index));
  };

  // Calculate total for all items
  const calculateTotal = () => {
    return saleItems.reduce((sum, item) => sum + item.total, 0);
  };

  // Clear draft data after successfully creating an invoice
  const clearDraftSaleData = () => {
    localStorage.removeItem('draftSaleItems');
    localStorage.removeItem('draftSaleNotes');
    localStorage.removeItem('draftPaymentMethod');
    localStorage.removeItem('draftSaleDate');
    localStorage.removeItem('draftSaleTimestamp');
    console.log('Cleared draft sale data from localStorage');
  };

  // Track payment method usage when creating an invoice
  const trackPaymentMethodUsage = (method: string) => {
    try {
      const stats = JSON.parse(localStorage.getItem('paymentMethodStats') || '{}');
      
      // Increment the count for this method
      stats[method] = (stats[method] || 0) + 1;
      
      localStorage.setItem('paymentMethodStats', JSON.stringify(stats));
      console.log(`Updated payment method stats for ${method}`);
    } catch (error) {
      console.error('Error tracking payment method usage:', error);
    }
  };

  // Handle creating a new invoice
  const handleCreateInvoice = async () => {
    if (!selectedCustomer) {
      setSnackbar({ open: true, message: 'Please select or add a customer', severity: 'error' });
      return;
    }

    if (saleItems.length === 0) {
      setSnackbar({ open: true, message: 'Please add at least one item', severity: 'error' });
      return;
    }

    try {
      setLoading(true);
      
      // Debug logs to check customer data
      console.log("Selected Customer:", selectedCustomer);
      console.log("Customer ID:", selectedCustomer.id);
      
      // Validate date - ensure it's not in the future
      const safeDate = date || new Date();
      const currentDate = new Date();
      if (safeDate > currentDate) {
        console.warn('Correcting future date to current date for invoice');
        safeDate.setFullYear(currentDate.getFullYear());
        safeDate.setMonth(currentDate.getMonth());
        safeDate.setDate(currentDate.getDate());
      }
      
      // Make sure we have a valid customer ID
      if (!selectedCustomer.id) {
        throw new Error('Invalid customer ID. Please select a valid customer.');
      }
      
      // Prepare sale data for API
      const saleData = {
        customerId: selectedCustomer.id,
        date: safeDate,
        paymentMethod,
        notes,
        status: 'completed',
        paymentStatus: 'unpaid', // Default to unpaid
        items: saleItems.map(item => ({
          productId: item.product.id,
          quantity: item.quantity,
          price: item.price,
          includeFitting: item.includeFitting
        }))
      };
      
      // Additional debug logs
      console.log("Sale data being sent:", JSON.stringify(saleData, null, 2));
      
      // Save to database via API
      const result = await createSale(saleData);
      console.log('Sale created in database:', result);
      
      // Track payment method usage
      trackPaymentMethodUsage(paymentMethod);
      
      // Clear the draft data since we've successfully created the invoice
      clearDraftSaleData();
      
      // Reset form
      setSelectedCustomer(null);
      setSaleItems([]);
      setDate(new Date());
      setPaymentMethod('cash');
      setNotes('');
      
      setSnackbar({ 
        open: true, 
        message: 'Invoice created successfully and saved to database', 
        severity: 'success' 
      });
      
      // Switch to invoices tab and refresh the invoices from the API
      setTabValue(1);
      
      // Refresh the invoices list from the API
      try {
        console.log("Refreshing invoices from API after creation...");
        const refreshedData = await getAllSales();
        
        if (Array.isArray(refreshedData)) {
          // Process sales from API into the Invoice format
          const processedSales = refreshedData.map(sale => {
            const customerData = sale.Customer || { name: 'Unknown Customer', phone: 'N/A' };
            
            const processedItems = sale.SaleItems && Array.isArray(sale.SaleItems) 
              ? sale.SaleItems.map((item: any) => {
                  const productData = item.Product || {
                    id: 0,
                    carMake: 'Unknown',
                    model: 'Unknown',
                    itemPrice: 0,
                    fittingPrice: 0,
                    quantity: 0
                  };
                  
                  const safeProduct = {
                    ...productData,
                    displayMake: productData.carMakeObj?.name || productData.carMake || 'Unknown',
                    displayModel: productData.carModelObj?.name || productData.model || 'Unknown'
                  };
                  
                  return {
                    id: item.id || 0,
                    product: safeProduct,
                    quantity: item.quantity || 1,
                    price: item.unitPrice || 0,
                    includeFitting: item.includeFitting || false,
                    total: item.totalPrice || 0
                  };
                }) 
              : [];
            
            return {
              id: sale.id || 0,
              invoiceNumber: sale.invoiceNumber || `INV-${Date.now()}`,
              customer: customerData,
              items: processedItems,
              date: new Date(sale.date || new Date()),
              totalAmount: sale.totalAmount || 0,
              paymentMethod: sale.paymentMethod || 'cash',
              notes: sale.notes || '',
              status: sale.status || 'completed',
              paymentStatus: sale.paymentStatus || 'unpaid'
            };
          });
          
          setInvoices(processedSales);
          console.log(`Refreshed ${processedSales.length} invoices from API after creating new invoice`);
        }
      } catch (refreshError) {
        console.error('Error refreshing invoices after creation:', refreshError);
      }
      
    } catch (error) {
      console.error('Error saving sale to database:', error);
      setSnackbar({ 
        open: true, 
        message: 'Failed to save sale to database. Please try again.', 
        severity: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  // Generate PDF invoice
  const generateInvoicePDF = (invoice: Invoice) => {
    try {
      // Create a new PDF document
      const doc = new jsPDF();
      
      // Company header - Arabic
      doc.setFontSize(22);
      doc.setTextColor(16, 29, 104);
      doc.text(t('app.companyName') || 'شركة وعد الدُنيا لأبناء العيان', 20, 20);
      
      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
      doc.text(t('app.companyAddress') || 'طريق الفلاح بجوار كوبري السريع , Tripoli, Libya', 20, 30);
      doc.text(`${t('sales.phone') || 'الهاتف'}: ${t('app.companyPhone') || '0917598838'}`, 20, 35);
      
      // Invoice details - Arabic
      doc.setFontSize(16);
      doc.setTextColor(16, 29, 104);
      doc.text(t('sales.invoices') || 'فاتورة', 150, 20, { align: "right" });
      
      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
      doc.text(`${t('sales.invoiceNum') || 'رقم الفاتورة'}: ${invoice.id}`, 150, 30, { align: "right" });
      doc.text(`${t('sales.date') || 'التاريخ'}: ${format(invoice.date, 'dd/MM/yyyy')}`, 150, 35, { align: "right" });
      doc.text(`${t('sales.paymentMethod') || 'طريقة الدفع'}: ${t(`sales.${invoice.paymentMethod}`) || invoice.paymentMethod}`, 150, 40, { align: "right" });
      doc.text(`${t('sales.paymentStatus.title') || 'حالة الدفع'}: ${invoice.paymentStatus === 'paid' ? (t('sales.paymentStatus.paid') || 'مدفوع') : (t('sales.paymentStatus.unpaid') || 'غير مدفوع')}`, 150, 45, { align: "right" });
      
      // Customer information - Arabic
      doc.setFontSize(12);
      doc.text(t('sales.billTo') || 'فاتورة إلى:', 20, 55);
      
      doc.setFontSize(10);
      doc.text(invoice.customer.name, 20, 62);
      doc.text(`${t('sales.phone') || 'الهاتف'}: ${invoice.customer.phone}`, 20, 67);
      
      // Vehicle details if available - Arabic
      if (invoice.customer.carMake) {
        let vehicleText = `${t('sales.vehicle') || 'السيارة'}: ${invoice.customer.carMake}`;
        if (invoice.customer.carModel) {
          vehicleText += ` ${invoice.customer.carModel}`;
        }
        doc.text(vehicleText, 20, 72);
      }
      
      // Items table - Arabic headers
      const itemsTableColumn = [
        t('sales.product') || 'المنتج',
        t('sales.quantity') || 'الكمية',
        t('sales.price') || 'السعر',
        t('sales.fitting') || 'التركيب',
        t('sales.total') || 'المجموع'
      ];
      const itemsTableRows = invoice.items.map(item => [
        `${(item.product.displayMake || '')} ${(item.product.displayModel || '')} - ${item.product.glassModel || 'زجاج'}`,
        item.quantity.toString(),
        `د.ل${item.price.toFixed(2)}`,
        item.includeFitting ? (t('sales.yes') || 'نعم') : (t('sales.no') || 'لا'),
        `د.ل${item.total.toFixed(2)}`
      ]);
      
      // Use the imported autoTable function instead of doc.autoTable
      autoTable(doc, {
        head: [itemsTableColumn],
        body: itemsTableRows,
        startY: invoice.customer.carMake ? 80 : 75,
        theme: 'striped',
        headStyles: { fillColor: [16, 29, 104], textColor: [255, 255, 255] },
        margin: { top: 20 }
      });
      
      // Get final Y position from the table
      const finalY = (doc as any).lastAutoTable?.finalY || 120;
      
      // Total amount - Arabic
      doc.setFontSize(12);
      doc.text(`${t('sales.total') || 'المجموع'}: د.ل${invoice.totalAmount.toFixed(2)}`, 150, finalY + 10, { align: "right" });
      
      // Notes if available - Arabic
      if (invoice.notes) {
        doc.setFontSize(12);
        doc.text(t('sales.notes') || 'ملاحظات:', 20, finalY + 20);
        
        doc.setFontSize(10);
        doc.text(invoice.notes, 20, finalY + 27);
      }
      
      // Footer - Arabic
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text(t('sales.thankYou') || 'شكراً لتعاملك معنا!', 105, finalY + 40, { align: "center" });
      
      // Save the PDF
      doc.save(`Invoice_${invoice.id}_${invoice.customer.name.replace(/\s+/g, '_')}.pdf`);
      
      // Show success alert
      setSnackbar({
        open: true,
        message: "PDF invoice generated successfully!",
        severity: "success"
      });
      
    } catch (error) {
      console.error("Error generating PDF:", error);
      setSnackbar({
        open: true,
        message: "Failed to generate PDF invoice. Please try again.",
        severity: "error"
      });
    }
  };

  // Handle print invoice button click
  const handlePrintInvoice = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setShowInvoicePreview(true);
  };

  // Handle confirm print invoice
  const handleConfirmPrintInvoice = () => {
    if (selectedInvoice) {
      generateInvoicePDF(selectedInvoice);
      setShowInvoicePreview(false);
    }
  };

  const handleDeleteSale = async () => {
    if (!saleToDelete) return;

    try {
      setLoading(true);
      await deleteSale(saleToDelete.id);
      setSnackbar({ 
        open: true, 
        message: t('sales.saleDeleted') || 'Sale deleted successfully', 
        severity: 'success' 
      });
      setDeleteDialogOpen(false);
      setSaleToDelete(null);
      
      // Refresh the sales list
      const data = await getAllSales();
      if (Array.isArray(data) && data.length > 0) {
        const processedSales = data.map(sale => {
          const customerData = sale.Customer || { name: 'Unknown Customer', phone: 'N/A' };
          const processedItems = sale.SaleItems && Array.isArray(sale.SaleItems) 
            ? sale.SaleItems.map((item: any) => {
                const productData = item.Product || {
                  id: 0,
                  carMake: 'Unknown',
                  model: 'Unknown',
                  itemPrice: 0,
                  fittingPrice: 0,
                  quantity: 0
                };
                const safeProduct = {
                  ...productData,
                  displayMake: productData.carMakeObj?.name || productData.carMake || 'Unknown',
                  displayModel: productData.carModelObj?.name || productData.model || 'Unknown'
                };
                return {
                  id: item.id || 0,
                  product: safeProduct,
                  quantity: item.quantity || 1,
                  price: item.unitPrice || 0,
                  includeFitting: item.includeFitting || false,
                  total: item.totalPrice || 0
                };
              }) 
            : [];
          return {
            id: sale.id || 0,
            invoiceNumber: sale.invoiceNumber || `INV-${Date.now()}`,
            customer: customerData,
            items: processedItems,
            date: new Date(sale.date || new Date()),
            totalAmount: sale.totalAmount || 0,
            paymentMethod: sale.paymentMethod || 'cash',
            notes: sale.notes || '',
            status: sale.status || 'completed',
            paymentStatus: sale.paymentStatus || 'unpaid'
          };
        });
        setInvoices(processedSales);
      } else {
        setInvoices([]);
      }
    } catch (err: any) {
      console.error('Failed to delete sale:', err);
      setSnackbar({ 
        open: true, 
        message: err.response?.data?.message || (t('sales.deleteError') || 'Failed to delete sale'), 
        severity: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDeleteDialog = (invoice: Invoice) => {
    setSaleToDelete(invoice);
    setDeleteDialogOpen(true);
  };

  const handleTogglePaymentStatus = async (invoice: Invoice) => {
    try {
      setLoading(true);
      const newPaymentStatus = invoice.paymentStatus === 'paid' ? 'unpaid' : 'paid';
      
      // Update local state immediately for better UX
      setInvoices(prevInvoices => 
        prevInvoices.map(inv => 
          inv.id === invoice.id 
            ? { ...inv, paymentStatus: newPaymentStatus }
            : inv
        )
      );
      
      // Call API to update payment status
      await updateSale(invoice.id, { paymentStatus: newPaymentStatus });
      
      // If payment is cash and marked as paid, track it for cash management
      if (newPaymentStatus === 'paid' && invoice.paymentMethod === 'cash') {
        // TODO: Integrate with cash management API to record cash transaction
        console.log('Cash payment recorded:', invoice.totalAmount);
      }
      
      setSnackbar({
        open: true,
        message: newPaymentStatus === 'paid' 
          ? (t('sales.paymentStatus.markedAsPaid') || 'Invoice marked as paid')
          : (t('sales.paymentStatus.markedAsUnpaid') || 'Invoice marked as unpaid'),
        severity: 'success'
      });
    } catch (error) {
      console.error('Error updating payment status:', error);
      // Revert local state on error
      setInvoices(prevInvoices => 
        prevInvoices.map(inv => 
          inv.id === invoice.id 
            ? { ...inv, paymentStatus: invoice.paymentStatus }
            : inv
        )
      );
      setSnackbar({
        open: true,
        message: 'Failed to update payment status',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSaleToDelete(null);
  };

  const handleDeleteCustomer = async () => {
    if (!customerToDelete || !customerToDelete.id) return;

    try {
      setLoading(true);
      await deleteCustomer(customerToDelete.id);
      setSnackbar({ 
        open: true, 
        message: t('customer.deleted') || 'Customer deleted successfully', 
        severity: 'success' 
      });
      setDeleteCustomerDialogOpen(false);
      setCustomerToDelete(null);
      
      // Clear selected customer if it was the deleted one
      if (selectedCustomer?.id === customerToDelete.id) {
        setSelectedCustomer(null);
      }
      
      // Refresh the customers list
      const data = await getAllCustomers();
      if (Array.isArray(data)) {
        setCustomers(data);
      }
    } catch (err: any) {
      console.error('Failed to delete customer:', err);
      const errorMessage = err.response?.data?.message || (t('customer.deleteError') || 'Failed to delete customer');
      setSnackbar({ 
        open: true, 
        message: errorMessage, 
        severity: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDeleteCustomerDialog = (customer: Customer) => {
    if (!customer.id) {
      setSnackbar({ 
        open: true, 
        message: t('customer.invalidCustomer') || 'Invalid customer', 
        severity: 'error' 
      });
      return;
    }
    setCustomerToDelete(customer);
    setDeleteCustomerDialogOpen(true);
  };

  const handleCloseDeleteCustomerDialog = () => {
    setDeleteCustomerDialogOpen(false);
    setCustomerToDelete(null);
  };

  // Apply filters to invoices
  const getFilteredInvoices = () => {
    return invoices.filter(invoice => {
      // Filter by status
      if (invoiceFilters.status !== 'all' && invoice.status !== invoiceFilters.status) {
        return false;
      }
      
      // Filter by date range
      if (invoiceFilters.startDate && new Date(invoice.date) < invoiceFilters.startDate) {
        return false;
      }
      
      if (invoiceFilters.endDate) {
        // Add one day to end date to include the entire day
        const endDatePlusOne = new Date(invoiceFilters.endDate);
        endDatePlusOne.setDate(endDatePlusOne.getDate() + 1);
        
        if (new Date(invoice.date) > endDatePlusOne) {
          return false;
        }
      }
      
      return true;
    });
  };

  // Update the draft save function to include a timestamp
  const saveDraftSaleData = () => {
    try {
      if (saleItems.length > 0) {
        localStorage.setItem('draftSaleItems', JSON.stringify(saleItems));
        localStorage.setItem('draftSaleNotes', notes);
        localStorage.setItem('draftPaymentMethod', paymentMethod);
        localStorage.setItem('draftSaleDate', date ? date.toISOString() : new Date().toISOString());
        localStorage.setItem('draftSaleTimestamp', Date.now().toString());
        console.log('Saved draft sale data to localStorage');
      }
    } catch (error) {
      console.error('Error saving draft sale data to localStorage:', error);
    }
  };

  // Auto-save draft data when sale items change
  useEffect(() => {
    // Only save if there are items and we're on the sales tab
    if (saleItems.length > 0 && tabValue === 0) {
      saveDraftSaleData();
    }
  }, [saleItems, notes, paymentMethod, date]);

  // Load draft sale items from localStorage on component mount
  const loadDraftSaleData = () => {
    try {
      const savedDraftItems = localStorage.getItem('draftSaleItems');
      const savedDraftNotes = localStorage.getItem('draftSaleNotes');
      const savedDraftPaymentMethod = localStorage.getItem('draftPaymentMethod');
      const savedDraftDate = localStorage.getItem('draftSaleDate');
      const draftTimestamp = localStorage.getItem('draftSaleTimestamp');
      
      // Check if the draft is too old (7 days)
      if (draftTimestamp) {
        const timestamp = parseInt(draftTimestamp, 10);
        const now = Date.now();
        const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
        
        if (now - timestamp > sevenDaysMs) {
          console.log('Draft sale data is too old (>7 days), not restoring');
          clearDraftSaleData();
          return;
        }
      }
      
      if (savedDraftItems) {
        const parsedItems = JSON.parse(savedDraftItems);
        if (Array.isArray(parsedItems) && parsedItems.length > 0) {
          setSaleItems(parsedItems);
          console.log(`Loaded ${parsedItems.length} draft sale items from localStorage`);
          
          // Show notification to user
          setSnackbar({
            open: true,
            message: 'Draft sale items were restored from your last session',
            severity: 'info' as 'info'
          });
        }
      }
      
      if (savedDraftNotes) {
        setNotes(savedDraftNotes);
      }
      
      if (savedDraftPaymentMethod) {
        setPaymentMethod(savedDraftPaymentMethod);
      }
      
      if (savedDraftDate) {
        setDate(new Date(savedDraftDate));
      }
    } catch (error) {
      console.error('Error loading draft sale data from localStorage:', error);
      // If there's an error, clear the corrupted data
      clearDraftSaleData();
    }
  };

  // Load form defaults from localStorage
  const loadFormDefaults = () => {
    try {
      // Get default payment method
      const paymentMethodStats = localStorage.getItem('paymentMethodStats');
      if (paymentMethodStats) {
        const stats = JSON.parse(paymentMethodStats);
        
        // Find the most frequently used payment method
        let mostFrequentMethod = 'cash';
        let highestCount = 0;
        
        Object.entries(stats).forEach(([method, count]: [string, any]) => {
          if (count > highestCount) {
            mostFrequentMethod = method;
            highestCount = count;
          }
        });
        
        // Only set if it has been used before
        if (highestCount > 0) {
          setPaymentMethod(mostFrequentMethod);
          console.log(`Set default payment method to most frequent: ${mostFrequentMethod}`);
        }
      }
    } catch (error) {
      console.error('Error loading payment method stats from localStorage:', error);
    }
  };

  // Add a refresh function for invoices tab
  const refreshInvoicesList = async () => {
    try {
      setLoading(true);
      console.log("Manually refreshing invoices from API...");
      const data = await getAllSales();
      
      if (Array.isArray(data)) {
        // Process sales from API into the Invoice format
        const processedSales = data.map(sale => {
          const customerData = sale.Customer || { name: 'Unknown Customer', phone: 'N/A' };
          
          const processedItems = sale.SaleItems && Array.isArray(sale.SaleItems) 
            ? sale.SaleItems.map((item: any) => {
                const productData = item.Product || {
                  id: 0,
                  carMake: 'Unknown',
                  model: 'Unknown',
                  itemPrice: 0,
                  fittingPrice: 0,
                  quantity: 0
                };
                
                const safeProduct = {
                  ...productData,
                  displayMake: productData.carMakeObj?.name || productData.carMake || 'Unknown',
                  displayModel: productData.carModelObj?.name || productData.model || 'Unknown'
                };
                
                return {
                  id: item.id || 0,
                  product: safeProduct,
                  quantity: item.quantity || 1,
                  price: item.unitPrice || 0,
                  includeFitting: item.includeFitting || false,
                  total: item.totalPrice || 0
                };
              }) 
            : [];
          
          return {
            id: sale.id || 0,
            invoiceNumber: sale.invoiceNumber || `INV-${Date.now()}`,
            customer: customerData,
            items: processedItems,
            date: new Date(sale.date || new Date()),
            totalAmount: sale.totalAmount || 0,
            paymentMethod: sale.paymentMethod || 'cash',
            notes: sale.notes || '',
            status: sale.status || 'completed',
            paymentStatus: sale.paymentStatus || 'unpaid'
          };
        });
        
        setInvoices(processedSales);
        console.log(`Refreshed ${processedSales.length} invoices from API`);
        
        setSnackbar({
          open: true,
          message: 'Invoice list refreshed from database',
          severity: 'success'
        });
      }
    } catch (error) {
      console.error('Error refreshing invoices from API:', error);
      setSnackbar({ 
        open: true, 
        message: 'Failed to refresh invoices from database', 
        severity: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Box sx={{ 
        borderBottom: 1, 
        borderColor: 'divider', 
        mb: 4,
        '& .MuiTabs-indicator': {
          backgroundColor: '#101D68',
          height: 3,
          borderRadius: '3px 3px 0 0',
        },
      }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange} 
          aria-label="sales tabs" 
          sx={{ 
            direction: isRTL ? 'rtl' : 'ltr',
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.9375rem',
              minHeight: 56,
              '&.Mui-selected': {
                color: '#101D68',
              },
            },
          }}
        >
          <Tab icon={<ReceiptIcon />} label={t('sales.newSale')} />
          <Tab icon={<SearchIcon />} label={t('sales.invoicesTab')} />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <Box sx={{ mb: 4 }}>
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 700,
              mb: 0.5,
              color: '#101D68',
              textAlign: isRTL ? 'right' : 'left' 
            }}
          >
            {t('sales.title')}
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              color: '#6B7280',
              textAlign: isRTL ? 'right' : 'left' 
            }}
          >
            Create and manage sales transactions
          </Typography>
        </Box>

        <Grid container spacing={3} direction={isRTL ? 'row-reverse' : 'row'}>
          <Grid size={{ xs: 12, md: 8 }}>
            <Paper sx={{ 
              p: 3, 
              mb: 3,
              borderRadius: 3,
              boxShadow: '0px 2px 8px rgba(16, 29, 104, 0.08)',
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', flexDirection: isRTL ? 'row-reverse' : 'row', mb: 3, alignItems: 'center' }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    textAlign: isRTL ? 'right' : 'left',
                    fontWeight: 600,
                    color: '#101D68',
                  }}
                >
                  {t('sales.items')}
                </Typography>
                <Button
                  variant="contained"
                  startIcon={!isRTL ? <AddIcon /> : null}
                  endIcon={isRTL ? <AddIcon /> : null}
                  onClick={() => setOpenProductDialog(true)}
                  sx={{
                    background: 'linear-gradient(135deg, #101D68 0%, #2A3A8A 100%)',
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    boxShadow: '0px 4px 12px rgba(16, 29, 104, 0.3)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #0A1445 0%, #101D68 100%)',
                      boxShadow: '0px 6px 16px rgba(16, 29, 104, 0.4)',
                    },
                  }}
                >
                  {t('sales.addItem')}
                </Button>
              </Box>

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ textAlign: isRTL ? 'right' : 'left' }}>{t('sales.product')}</TableCell>
                      <TableCell align={isRTL ? 'left' : 'right'}>{t('sales.quantity')}</TableCell>
                      <TableCell align={isRTL ? 'left' : 'right'}>{t('sales.price')}</TableCell>
                      <TableCell align="center">{t('sales.fitting')}</TableCell>
                      <TableCell align={isRTL ? 'left' : 'right'}>{t('sales.total')}</TableCell>
                      <TableCell align={isRTL ? 'left' : 'right'}>{t('sales.actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {saleItems.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell sx={{ textAlign: isRTL ? 'right' : 'left' }}>
                          {(item.product.displayMake || '')} {(item.product.displayModel || '')}
                        </TableCell>
                        <TableCell align={isRTL ? 'left' : 'right'}>{item.quantity}</TableCell>
                        <TableCell align={isRTL ? 'left' : 'right'}>د.ل{item.price.toFixed(2)}</TableCell>
                        <TableCell align="center">{item.includeFitting ? t('sales.yes') : t('sales.no')}</TableCell>
                        <TableCell align={isRTL ? 'left' : 'right'}>د.ل{item.total.toFixed(2)}</TableCell>
                        <TableCell align={isRTL ? 'left' : 'right'}>
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveItem(index)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                    {saleItems.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          {t('sales.noItems')}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ textAlign: isRTL ? 'right' : 'left' }}>
                {t('sales.customerInfo')}
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, flexDirection: isRTL ? 'row-reverse' : 'row', gap: 1 }}>
                {/* Customer Selection Autocomplete */}
                <Autocomplete
                  options={customers}
                  getOptionLabel={(option) => `${option.name} (${option.phone})`}
                  value={selectedCustomer}
                  onChange={(_, newValue) => {
                    if (newValue) {
                      console.log("Customer selected:", newValue);
                      console.log("Customer ID:", newValue.id);
                      // Ensure we're using a properly structured customer with a valid ID
                      setSelectedCustomer(newValue);
                      
                      // Set car details if available
                      if (newValue.carMake) {
                        const make = carMakes.find(m => m.name === newValue.carMake);
                        if (make) {
                          setSelectedCarMake(make);
                          console.log("Car make set:", make);
                        }
                      }
                      
                      if (newValue.carMake && newValue.carModel) {
                        const make = carMakes.find(m => m.name === newValue.carMake);
                        if (make) {
                          const model = carModels.find(m => m.name === newValue.carModel && m.carMakeId === make.id);
                          if (model) {
                            setSelectedCarModel(model);
                            console.log("Car model set:", model);
                          }
                        }
                      }
                      
                    } else {
                      setSelectedCustomer(null);
                    }
                  }}
                  onInputChange={(_, value) => handleCustomerSearch(value)}
                  renderInput={(params) => (
                    <TextField 
                      {...params} 
                      label={t('sales.searchCustomer')}
                      fullWidth 
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loading ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                      sx={{ textAlign: isRTL ? 'right' : 'left' }}
                    />
                  )}
                  sx={{ flexGrow: 1 }}
                />
                {selectedCustomer && selectedCustomer.id && isManager && (
                  <IconButton
                    color="error"
                    onClick={() => handleOpenDeleteCustomerDialog(selectedCustomer)}
                    title={t('customer.delete') || 'Delete Customer'}
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
                <Button 
                  variant="outlined"
                  startIcon={!isRTL ? <PersonIcon /> : null}
                  endIcon={isRTL ? <PersonIcon /> : null}
                  onClick={() => setOpenNewCustomerDialog(true)}
                >
                  {t('sales.newCustomer')}
                </Button>
              </Box>

              {/* New Customer Dialog */}
              <Dialog 
                open={openNewCustomerDialog} 
                onClose={() => setOpenNewCustomerDialog(false)}
                fullWidth
              >
                <DialogTitle sx={{ textAlign: isRTL ? 'right' : 'left' }}>{t('sales.addNewCustomer')}</DialogTitle>
                <DialogContent dividers>
                  <Grid container spacing={2} direction={isRTL ? 'row-reverse' : 'row'}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        label={t('customer.form.firstName')}
                        fullWidth
                        value={newCustomer.firstName}
                        onChange={(e) => setNewCustomer({ ...newCustomer, firstName: e.target.value, name: `${e.target.value} ${newCustomer.lastName}`.trim() })}
                        margin="normal"
                        InputProps={{ 
                          sx: { textAlign: isRTL ? 'right' : 'left' }
                        }}
                        sx={{ textAlign: isRTL ? 'right' : 'left' }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        label={t('customer.form.lastName')}
                        fullWidth
                        value={newCustomer.lastName}
                        onChange={(e) => setNewCustomer({ ...newCustomer, lastName: e.target.value, name: `${newCustomer.firstName} ${e.target.value}`.trim() })}
                        margin="normal"
                        InputProps={{ 
                          sx: { textAlign: isRTL ? 'right' : 'left' }
                        }}
                        sx={{ textAlign: isRTL ? 'right' : 'left' }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        label={t('customer.form.phone')}
                        fullWidth
                        value={newCustomer.phone}
                        onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })}
                        margin="normal"
                        InputProps={{ 
                          sx: { textAlign: isRTL ? 'right' : 'left' }
                        }}
                        sx={{ textAlign: isRTL ? 'right' : 'left' }}
                      />
                    </Grid>
                  </Grid>
                </DialogContent>
                <DialogActions sx={{ display: 'flex', justifyContent: isRTL ? 'flex-start' : 'flex-end' }}>
                  <Button onClick={() => setOpenNewCustomerDialog(false)}>{t('common.close')}</Button>
                  <Button onClick={handleAddCustomer} color="primary" disabled={!newCustomer.firstName || !newCustomer.phone}>
                    {t('sales.addCustomer')}
                  </Button>
                </DialogActions>
              </Dialog>

              <Typography variant="subtitle1" sx={{ mt: 2, textAlign: isRTL ? 'right' : 'left' }}>
                {t('sales.date')}
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  value={date}
                  onChange={(newValue) => setDate(newValue)}
                  maxDate={new Date()}
                  slotProps={{ 
                    textField: { 
                      fullWidth: true, 
                      margin: "dense",
                      InputProps: {
                        sx: { textAlign: isRTL ? 'right' : 'left' }
                      }
                    } 
                  }}
                />
              </LocalizationProvider>

              <Typography variant="subtitle1" sx={{ mt: 2, textAlign: isRTL ? 'right' : 'left' }}>
                {t('sales.paymentMethod')}
              </Typography>
              <TextField
                select
                fullWidth
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                margin="dense"
                SelectProps={{
                  native: true,
                }}
                InputProps={{
                  sx: { textAlign: isRTL ? 'right' : 'left' }
                }}
              >
                <option value="cash">{t('sales.cash')}</option>
                <option value="creditCard">{t('sales.creditCard')}</option>
                <option value="debitCard">{t('sales.debitCard')}</option>
                <option value="bankTransfer">{t('sales.bankTransfer')}</option>
              </TextField>

              <Typography variant="subtitle1" sx={{ mt: 2, textAlign: isRTL ? 'right' : 'left' }}>
                {t('sales.notes')}
              </Typography>
              <TextField
                multiline
                rows={4}
                fullWidth
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                margin="dense"
                InputProps={{
                  sx: { textAlign: isRTL ? 'right' : 'left' }
                }}
              />

              <Box sx={{ mt: 3 }}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: saleItems.length > 0 ? 8 : 12 }}>
                    <Button
                      variant="contained"
                      fullWidth
                      size="large"
                      onClick={handleCreateInvoice}
                      disabled={!selectedCustomer || saleItems.length === 0 || loading}
                      startIcon={!isRTL && !loading ? <ReceiptIcon /> : null}
                      endIcon={isRTL ? <ReceiptIcon /> : null}
                    >
                      {loading ? <CircularProgress size={24} /> : t('sales.createInvoice')}
                    </Button>
                  </Grid>
                  
                  {saleItems.length > 0 && (
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <Button
                        variant="outlined"
                        fullWidth
                        size="large"
                        onClick={() => {
                          // Show confirmation dialog
                          if (window.confirm(t('sales.confirmClearDraft'))) {
                            // Clear the draft data
                            clearDraftSaleData();
                            
                            // Reset form
                            setSaleItems([]);
                            setNotes('');
                            setPaymentMethod('cash');
                            setDate(new Date());
                            
                            setSnackbar({
                              open: true,
                              message: t('sales.draftCleared'),
                              severity: 'info'
                            });
                          }
                        }}
                        startIcon={!isRTL ? <CloseIcon /> : null}
                        endIcon={isRTL ? <CloseIcon /> : null}
                        color="error"
                      >
                        {t('sales.discardDraft')}
                      </Button>
                    </Grid>
                  )}
                </Grid>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h4" gutterBottom sx={{ textAlign: isRTL ? 'right' : 'left', mb: 0 }}>
              {t('sales.invoices')}
            </Typography>
            <Button 
              variant="contained" 
              onClick={refreshInvoicesList}
              startIcon={!isRTL ? <SearchIcon /> : null}
              endIcon={isRTL ? <SearchIcon /> : null}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : t('common.refresh')}
            </Button>
          </Box>
          
          {/* Invoice filters */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Grid container spacing={2} alignItems="flex-end" direction={isRTL ? 'row-reverse' : 'row'}>
              <Grid size={{ xs: 12, sm: 3 }}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker 
                    label={t('sales.startDate')}
                    value={invoiceFilters.startDate}
                    onChange={(date) => setInvoiceFilters({
                      ...invoiceFilters, 
                      startDate: date
                    })}
                    slotProps={{ 
                      textField: { 
                        fullWidth: true, 
                        size: 'small',
                        InputProps: {
                          sx: { textAlign: isRTL ? 'right' : 'left' }
                        }
                      } 
                    }}
                  />
                </LocalizationProvider>
              </Grid>
              
              <Grid size={{ xs: 12, sm: 3 }}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker 
                    label={t('sales.endDate')}
                    value={invoiceFilters.endDate}
                    onChange={(date) => setInvoiceFilters({
                      ...invoiceFilters, 
                      endDate: date
                    })}
                    slotProps={{ 
                      textField: { 
                        fullWidth: true, 
                        size: 'small',
                        InputProps: {
                          sx: { textAlign: isRTL ? 'right' : 'left' }
                        }
                      } 
                    }}
                  />
                </LocalizationProvider>
              </Grid>
              
              <Grid size={{ xs: 12, sm: 3 }}>
                <TextField
                  select
                  label={t('sales.status')}
                  value={invoiceFilters.status}
                  onChange={(e) => setInvoiceFilters({
                    ...invoiceFilters,
                    status: e.target.value as 'all' | 'pending' | 'completed' | 'cancelled'
                  })}
                  fullWidth
                  size="small"
                  SelectProps={{
                    native: true,
                  }}
                  InputProps={{
                    sx: { textAlign: isRTL ? 'right' : 'left' }
                  }}
                >
                  <option value="all">{t('sales.all')}</option>
                  <option value="pending">{t('sales.pending')}</option>
                  <option value="completed">{t('sales.completed')}</option>
                  <option value="cancelled">{t('sales.cancelled')}</option>
                </TextField>
              </Grid>
              
              <Grid size={{ xs: 12, sm: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: isRTL ? 'flex-start' : 'flex-end' }}>
                  <Button 
                    variant="outlined"
                    onClick={() => setInvoiceFilters({
                      startDate: null,
                      endDate: null,
                      status: 'all'
                    })}
                    sx={{ mr: 1 }}
                  >
                    {t('sales.clearFilters')}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Paper>
          
          {/* Invoices list */}
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ textAlign: isRTL ? 'right' : 'left' }}>{t('sales.invoiceNum')}</TableCell>
                  <TableCell sx={{ textAlign: isRTL ? 'right' : 'left' }}>{t('customer.name')}</TableCell>
                  <TableCell sx={{ textAlign: isRTL ? 'right' : 'left' }}>{t('sales.date')}</TableCell>
                  <TableCell align={isRTL ? 'left' : 'right'}>{t('sales.total')}</TableCell>
                  <TableCell sx={{ textAlign: isRTL ? 'right' : 'left' }}>{t('sales.paymentStatus.title') || 'Payment Status'}</TableCell>
                  <TableCell sx={{ textAlign: isRTL ? 'right' : 'left' }}>{t('sales.status')}</TableCell>
                  <TableCell align={isRTL ? 'left' : 'right'}>{t('sales.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getFilteredInvoices().map((invoice) => (
                  <TableRow key={invoice.id}>
                    <TableCell sx={{ textAlign: isRTL ? 'right' : 'left' }}>#{invoice.id}</TableCell>
                    <TableCell sx={{ textAlign: isRTL ? 'right' : 'left' }}>{invoice.customer.name}</TableCell>
                    <TableCell sx={{ textAlign: isRTL ? 'right' : 'left' }}>{new Date(invoice.date).toLocaleDateString()}</TableCell>
                    <TableCell align={isRTL ? 'left' : 'right'}>د.ل{invoice.totalAmount.toFixed(2)}</TableCell>
                    <TableCell sx={{ textAlign: isRTL ? 'right' : 'left' }}>
                      <Chip
                        label={invoice.paymentStatus === 'paid' ? (t('sales.paymentStatus.paid') || 'Paid') : (t('sales.paymentStatus.unpaid') || 'Unpaid')}
                        color={invoice.paymentStatus === 'paid' ? 'success' : 'error'}
                        size="small"
                        onClick={() => isManager && handleTogglePaymentStatus(invoice)}
                        sx={{ cursor: isManager ? 'pointer' : 'default' }}
                      />
                    </TableCell>
                    <TableCell sx={{ textAlign: isRTL ? 'right' : 'left' }}>
                      <Box
                        component="span"
                        sx={{
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                          bgcolor: 
                            invoice.status === 'completed' ? 'success.light' :
                            invoice.status === 'pending' ? 'warning.light' :
                            'error.light',
                          color: 
                            invoice.status === 'completed' ? 'success.dark' :
                            invoice.status === 'pending' ? 'warning.dark' :
                            'error.dark',
                        }}
                      >
                        {t(`sales.${invoice.status}`)}
                      </Box>
                    </TableCell>
                    <TableCell align={isRTL ? 'left' : 'right'}>
                      <IconButton
                        size="small"
                        onClick={() => handlePrintInvoice(invoice)}
                        color="primary"
                        title={t('sales.print') || 'Print'}
                      >
                        <PrintIcon />
                      </IconButton>
                      {isManager && (
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDeleteDialog(invoice)}
                          color="error"
                          title={t('sales.delete') || 'Delete'}
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {getFilteredInvoices().length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      {t('sales.noItems')}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </TabPanel>

      {/* Product Dialog */}
      <Dialog
        open={openProductDialog}
        onClose={() => {
          setSelectedProduct(null);
          setQuantity(1);
          setIncludeFitting(false);
          setFilteredProducts([]);
          setSelectedCarMake(null);
          setSelectedCarModel(null);
          setSelectedGlassType('');
          setCustomerYear('');
          setOpenProductDialog(false);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ textAlign: isRTL ? 'right' : 'left' }}>{t('sales.selectProduct')}</DialogTitle>
        <DialogContent dividers>
          {/* Step 1: Select car make, model, and glass type */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('customer.carMake')}</InputLabel>
                <Select
                  value={selectedCarMake ? selectedCarMake.id : ''}
                  onChange={(e) => {
                    const makeId = e.target.value as number;
                    const make = carMakes.find(m => m.id === makeId) || null;
                    setSelectedCarMake(make);
                    setSelectedCarModel(null);
                    // Filter car models for this make
                    if (make) {
                      setFilteredCarModels(carModels.filter(model => model.carMakeId === makeId));
                    } else {
                      setFilteredCarModels([]);
                    }
                  }}
                  label={t('customer.carMake')}
                >
                  <MenuItem value="">
                    <em>{t('common.all')}</em>
                  </MenuItem>
                  {carMakes.map(make => (
                    <MenuItem key={make.id} value={make.id}>{make.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <FormControl fullWidth size="small" disabled={!selectedCarMake}>
                <InputLabel>{t('customer.carModel')}</InputLabel>
                <Select
                  value={selectedCarModel ? selectedCarModel.id : ''}
                  onChange={(e) => {
                    const modelId = e.target.value as number;
                    const model = filteredCarModels.find(m => m.id === modelId) || null;
                    setSelectedCarModel(model);
                  }}
                  label={t('customer.carModel')}
                >
                  <MenuItem value="">
                    <em>{t('common.all')}</em>
                  </MenuItem>
                  {filteredCarModels.map(model => (
                    <MenuItem key={model.id} value={model.id}>{model.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('inventory.form.glassType')}</InputLabel>
                <Select
                  value={selectedGlassType || ''}
                  onChange={(e) => setSelectedGlassType(e.target.value as string)}
                  label={t('inventory.form.glassType')}
                >
                  <MenuItem value="">
                    <em>{t('common.all')}</em>
                  </MenuItem>
                  <MenuItem value="front">{t('inventory.glassTypes.front')}</MenuItem>
                  <MenuItem value="back">{t('inventory.glassTypes.back')}</MenuItem>
                  <MenuItem value="passenger">{t('inventory.glassTypes.passenger')}</MenuItem>
                  <MenuItem value="driver">{t('inventory.glassTypes.driver')}</MenuItem>
                  <MenuItem value="quarter">{t('inventory.glassTypes.quarter')}</MenuItem>
                  <MenuItem value="sunroof">{t('inventory.glassTypes.sunroof')}</MenuItem>
                  <MenuItem value="other">{t('inventory.glassTypes.other')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Step 2: Enter year and show matching products */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                size="small"
                label={t('customer.carYear')}
                value={customerYear}
                onChange={(e) => setCustomerYear(e.target.value)}
                placeholder="e.g. 2018"
                InputProps={{
                  sx: { textAlign: isRTL ? 'right' : 'left' }
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 8 }}>
              <Button 
                variant="contained" 
                onClick={filterProductsByCompatibility}
                disabled={!selectedCarMake && !selectedCarModel && !selectedGlassType && !customerYear}
              >
                {t('common.filters')}
              </Button>
            </Grid>
          </Grid>

          {/* Product selection */}
          <Autocomplete
            options={getFilteredProducts()}
            getOptionLabel={(option) => `${option.displayMake} ${option.displayModel} - ${option.glassModel || 'Unknown'} (${option.year || 'N/A'} - ${option.coveredTo || 'N/A'})`}
            value={selectedProduct}
            onChange={(_, newValue) => setSelectedProduct(newValue)}
            renderInput={(params) => (
              <TextField 
                {...params} 
                label={t('sales.product')} 
                fullWidth 
                margin="normal" 
                InputProps={{
                  ...params.InputProps,
                  sx: { textAlign: isRTL ? 'right' : 'left' }
                }}
                sx={{ textAlign: isRTL ? 'right' : 'left' }}
              />
            )}
          />
          
          <TextField
            label={t('sales.quantity')}
            type="number"
            value={quantity}
            onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
            fullWidth
            margin="normal"
            InputProps={{ 
              inputProps: { min: 1, max: selectedProduct ? selectedProduct.quantity : 999 },
              sx: { textAlign: isRTL ? 'right' : 'left' }
            }}
            helperText={selectedProduct ? `${t('inventory.columns.qty')}: ${selectedProduct.quantity}` : ''}
            FormHelperTextProps={{
              sx: { textAlign: isRTL ? 'right' : 'left' }
            }}
          />
          
          <FormControlLabel
            control={
              <Checkbox
                checked={includeFitting}
                onChange={(e) => setIncludeFitting(e.target.checked)}
                disabled={selectedProduct?.fittingPrice === 0}
              />
            }
            label={`${t('sales.includeFitting')} (${selectedProduct ? '+د.ل' + (selectedProduct.fittingPrice || 0).toFixed(2) : '+د.ل0.00'})`}
            sx={{ 
              display: 'flex', 
              flexDirection: isRTL ? 'row-reverse' : 'row', 
              marginRight: isRTL ? 0 : 'auto',
              marginLeft: isRTL ? 'auto' : 0,
              alignItems: 'center'
            }}
          />
          
          {selectedProduct && (
            <Box sx={{ mt: 2, p: 2, border: '1px solid #eee', borderRadius: 1, textAlign: isRTL ? 'right' : 'left' }}>
              <Typography variant="subtitle2">Product Details:</Typography>
              <Typography variant="body2">
                Make: {selectedProduct.displayMake}<br />
                Model: {selectedProduct.displayModel}<br />
                Glass: {selectedProduct.glassModel || 'N/A'}<br />
                Year Range: {selectedProduct.year || 'N/A'} - {selectedProduct.coveredTo || 'N/A'}<br />
                {selectedProduct.glassType && <>Glass Type: {t(`inventory.glassTypes.${selectedProduct.glassType}`)}<br /></>}
                {selectedProduct.remark && <>Remark: {selectedProduct.remark}<br /></>}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: isRTL ? 'flex-start' : 'flex-end' }}>
          <Button onClick={() => setOpenProductDialog(false)}>
            {t('sales.cancel')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleAddItem}
            disabled={!selectedProduct}
          >
            {t('sales.add')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Invoice Preview Dialog */}
      <Dialog
        open={showInvoicePreview}
        onClose={() => setShowInvoicePreview(false)}
        fullWidth
        maxWidth="md"
      >
        {selectedInvoice && (
          <>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
              <Typography sx={{ textAlign: isRTL ? 'right' : 'left' }}>{t('sales.invoicePreview')}</Typography>
              <IconButton onClick={() => setShowInvoicePreview(false)}>
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              <Paper sx={{ p: 3, mb: 3, position: 'relative', textAlign: isRTL ? 'right' : 'left' }}>
                {/* Company details */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h4" gutterBottom color="primary" sx={{ fontFamily: isRTL ? '"Noto Sans Arabic", Tahoma, Arial' : 'inherit' }}>
                    {t('app.companyName') || 'شركة وعد الدُنيا لأبناء العيان'}
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: isRTL ? '"Noto Sans Arabic", Tahoma, Arial' : 'inherit' }}>
                    {t('app.companyAddress') || 'طريق الفلاح بجوار كوبري السريع , Tripoli, Libya'}
                  </Typography>
                  <Typography variant="body2">
                    {t('sales.phone') || 'الهاتف'}: {t('app.companyPhone') || '0917598838'}
                  </Typography>
                </Box>
                
                {/* Invoice details */}
                <Box sx={{ mb: 3, display: 'flex', justifyContent: isRTL ? 'flex-end' : 'space-between', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                  <Box>
                    <Typography variant="h6" gutterBottom color="primary">
                      {t('sales.invoiceDetails')}
                    </Typography>
                    <Typography variant="body2">{t('sales.invoiceNum')} {selectedInvoice.id}</Typography>
                    <Typography variant="body2">{t('sales.date')} {format(selectedInvoice.date, 'MMM dd, yyyy')}</Typography>
                    <Typography variant="body2">{t('sales.paymentMethod')} {t(`sales.${selectedInvoice.paymentMethod}`)}</Typography>
                  </Box>
                </Box>
                
                {/* Customer details */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" fontWeight="bold">{t('sales.billTo')}</Typography>
                  <Typography variant="body2">{selectedInvoice.customer.name}</Typography>
                  <Typography variant="body2">{t('sales.phone')} {selectedInvoice.customer.phone}</Typography>
                  {selectedInvoice.customer.carMake && (
                    <Typography variant="body2">
                      {t('sales.vehicle')} {selectedInvoice.customer.carMake} {selectedInvoice.customer.carModel || ''}
                    </Typography>
                  )}
                </Box>
                
                {/* Items table */}
                <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ textAlign: isRTL ? 'right' : 'left' }}>{t('sales.product')}</TableCell>
                        <TableCell sx={{ textAlign: isRTL ? 'right' : 'left' }}>{t('sales.quantity')}</TableCell>
                        <TableCell align={isRTL ? 'left' : 'right'}>{t('sales.price')}</TableCell>
                        <TableCell align="center">{t('sales.fitting')}</TableCell>
                        <TableCell align={isRTL ? 'left' : 'right'}>{t('sales.total')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedInvoice.items.map((item, index) => (
                        <TableRow key={index} sx={{ '&:nth-of-type(odd)': { bgcolor: 'action.hover' } }}>
                          <TableCell sx={{ textAlign: isRTL ? 'right' : 'left' }}>
                            {(item.product.displayMake || '')} {(item.product.displayModel || '')} - {item.product.glassModel || 'Glass'}
                          </TableCell>
                          <TableCell sx={{ textAlign: isRTL ? 'right' : 'left' }}>{item.quantity}</TableCell>
                          <TableCell align={isRTL ? 'left' : 'right'}>د.ل{item.price.toFixed(2)}</TableCell>
                          <TableCell align="center">{item.includeFitting ? t('sales.yes') : t('sales.no')}</TableCell>
                          <TableCell align={isRTL ? 'left' : 'right'}>د.ل{item.total.toFixed(2)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                
                {/* Total and notes */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                  <Box>
                    {selectedInvoice.notes && (
                      <>
                        <Typography variant="subtitle1" fontWeight="bold">{t('sales.notes')}</Typography>
                        <Typography variant="body2">{selectedInvoice.notes}</Typography>
                      </>
                    )}
                  </Box>
                  <Box>
                    <Typography variant="h6" fontWeight="bold">
                      {t('sales.total')}: د.ل{selectedInvoice.totalAmount.toFixed(2)}
                    </Typography>
                  </Box>
                </Box>
                
                {/* Thank you note */}
                <Typography variant="body2" align="center" color="text.secondary">
                  {t('sales.thankYou')}
                </Typography>
              </Paper>
            </DialogContent>
            <DialogActions sx={{ justifyContent: isRTL ? 'flex-start' : 'flex-end' }}>
              <Button onClick={() => setShowInvoicePreview(false)}>
                {t('sales.cancel')}
              </Button>
              <Button 
                variant="contained"
                color="primary"
                startIcon={!isRTL ? <PrintIcon /> : null}
                endIcon={isRTL ? <PrintIcon /> : null}
                onClick={handleConfirmPrintInvoice}
              >
                {t('sales.printInvoice')}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Delete Customer Confirmation Dialog */}
      <Dialog open={deleteCustomerDialogOpen} onClose={handleCloseDeleteCustomerDialog}>
        <DialogTitle sx={{ textAlign: isRTL ? 'right' : 'left' }}>
          {t('customer.confirmDelete') || 'Confirm Delete Customer'}
        </DialogTitle>
        <DialogContent sx={{ textAlign: isRTL ? 'right' : 'left' }}>
          <Typography>
            {t('customer.deleteMessage') || 'Are you sure you want to delete this customer?'}
            <br />
            <br />
            <strong>{t('customer.name') || 'Name'}: {customerToDelete?.name}</strong>
            <br />
            {t('customer.phone') || 'Phone'}: {customerToDelete?.phone}
            <br />
            <br />
            {t('customer.deleteWarning') || 'This action cannot be undone. Customers with existing sales cannot be deleted.'}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ display: 'flex', justifyContent: isRTL ? 'flex-start' : 'flex-end' }}>
          <Button onClick={handleCloseDeleteCustomerDialog}>
            {t('common.cancel') || 'Cancel'}
          </Button>
          <Button onClick={handleDeleteCustomer} color="error" variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={20} /> : (t('common.delete') || 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Sale Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle sx={{ textAlign: isRTL ? 'right' : 'left' }}>
          {t('sales.confirmDelete') || 'Confirm Delete'}
        </DialogTitle>
        <DialogContent sx={{ textAlign: isRTL ? 'right' : 'left' }}>
          <Typography>
            {t('sales.deleteSaleMessage') || 'Are you sure you want to delete this sale?'}
            <br />
            <br />
            <strong>{t('sales.invoiceNum') || 'Invoice'}: #{saleToDelete?.id}</strong>
            <br />
            {t('sales.customer') || 'Customer'}: {saleToDelete?.customer.name}
            <br />
            {t('sales.total') || 'Total'}: د.ل{saleToDelete?.totalAmount.toFixed(2)}
            <br />
            <br />
            {t('sales.deleteWarning') || 'This action cannot be undone. Inventory quantities will be restored.'}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ display: 'flex', justifyContent: isRTL ? 'flex-start' : 'flex-end' }}>
          <Button onClick={handleCloseDeleteDialog}>
            {t('common.cancel') || 'Cancel'}
          </Button>
          <Button onClick={handleDeleteSale} color="error" variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={20} /> : (t('common.delete') || 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Add loading indicator */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
          <CircularProgress />
        </Box>
      )}
    </Box>
  );
};

export default Sales; 